import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Send, Linkedin, Youtube } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const TYPES = ["Live Course Enrollment", "Self-Paced Course Access", "Corporate Team Training", "S/4HANA Consulting / Advisory", "1-on-1 Mentorship"];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", inquiry_type: TYPES[0], company_or_role: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry sent", { description: "Vikram's team will get back to you within one business day." });
      setForm({ name: "", email: "", inquiry_type: TYPES[0], company_or_role: "", message: "" });
    } catch (err) {
      toast.error("Could not send inquiry", { description: "Please check the fields and try again." });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#ffffff] border border-slate-200 rounded-xl px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60 transition-colors";

  return (
    <section id="contact" className="relative py-28 lg:py-36" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-mono2 text-xs sm:text-sm uppercase tracking-[0.25em] text-amber-600 mb-5"
          >
            Chapter 05 / Get in Touch
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight"
          >
            Ready to Master S/4HANA — or Transform Your Enterprise?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
            className="mt-6 text-sm sm:text-base text-slate-600 leading-relaxed max-w-md"
          >
            Course enrollments, corporate team training, or high-impact SAP consulting — send the details and you'll hear back within one business day.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.3 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <a
              href="https://www.linkedin.com/in/vikram-fotani-525a42119/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 border border-slate-200 rounded-full px-6 py-3.5 text-sm text-slate-700 hover:border-sky-400/50 hover:text-sky-700 transition-colors duration-300"
              data-testid="contact-linkedin-link"
            >
              <Linkedin size={16} /> Connect on LinkedIn
            </a>
            <a
              href="https://www.youtube.com/@VikramFotani"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 border border-slate-200 rounded-full px-6 py-3.5 text-sm text-slate-700 hover:border-red-400/60 hover:text-red-600 transition-colors duration-300"
              data-testid="contact-youtube-link"
            >
              <Youtube size={16} /> Watch on YouTube
            </a>
          </motion.div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
          onSubmit={submit}
          className="lg:col-span-7 rounded-3xl border border-slate-200 bg-[#F8FAFC] p-7 lg:p-10 space-y-4"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Full name *"
              className={inputCls}
              data-testid="contact-form-name-input"
            />
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Work email *"
              className={inputCls}
              data-testid="contact-form-email-input"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <select
              value={form.inquiry_type}
              onChange={(e) => setForm({ ...form, inquiry_type: e.target.value })}
              className={inputCls}
              data-testid="contact-form-select-inquiry"
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <input
              value={form.company_or_role}
              onChange={(e) => setForm({ ...form, company_or_role: e.target.value })}
              placeholder="Company / current role"
              className={inputCls}
              data-testid="contact-form-company-input"
            />
          </div>
          <textarea
            required
            rows={5}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Tell us about your S/4HANA goals or course requirements... *"
            className={`${inputCls} resize-none`}
            data-testid="contact-form-message-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-amber-400 text-slate-900 font-semibold text-sm px-9 py-4 rounded-full hover:bg-amber-500 disabled:opacity-60 transition-colors duration-300"
            data-testid="contact-form-submit-button"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            {loading ? "Sending..." : "Send Inquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
